import { useCallback, useState } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import { Button, Flex, Heading, Text } from '@radix-ui/themes'
import { MergeGuestSessionMutation } from '__generated__/graphql'
import { MERGE_GUEST_SESSION } from 'api/guest-session/merge-guest-session'
import { DataTestIDs } from 'shared/constants'
import { CupColoredIcon, UpDownNavIcon } from 'shared/icons'
import upWinnerDecorationsPath from 'shared/icons/background-element-icons/up-down-winner-decorations.svg'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { XyroDialog, XyroLoading } from 'shared/ui'
import { showNotificationToast } from 'shared/utils/notify'
import { guestDemoRoundsVar, type GuestDemoRounds } from '../store'
import {
  describeClaimResult,
  describeDemoRounds,
  getClaimErrorMessage
} from '../utils/describe-claim'
import styles from './guest-claim-dialog.module.scss'

/** Leaderboards the claim can change; only the active queries refetch. */
const REFETCH_AFTER_CLAIM = [
  'getUpDownAiLeaderboard',
  'getMyUpDownAiLeaderboardPosition',
  'getBullsEyeLeaderboard',
  'getMyBullsEyeLeaderboardPosition'
]

const finishClaim = (
  result: MergeGuestSessionMutation['mergeGuestSession'] | undefined,
  rounds: GuestDemoRounds
) => {
  isGuestClaimDialogOpenVar(false)
  if (!result) return

  showNotificationToast(
    result.alreadyClaimed ?
      {
        type: 'info',
        title: 'Demo results already claimed',
        description: 'They are on the leaderboards since your earlier sign-in.'
      }
    : describeClaimResult(result, rounds)
  )
}

export const GuestClaimDialog = () => {
  const isOpen = useReactiveVar(isGuestClaimDialogOpenVar)
  const rounds = useReactiveVar(guestDemoRoundsVar)
  const [error, setError] = useState<string | null>(null)
  const [mergeGuestSession, { loading }] =
    useMutation<MergeGuestSessionMutation>(MERGE_GUEST_SESSION)

  const handleClaim = useCallback(async () => {
    setError(null)
    try {
      const { data } = await mergeGuestSession({
        refetchQueries: REFETCH_AFTER_CLAIM
      })
      finishClaim(data?.mergeGuestSession, rounds)
    } catch (caught) {
      // NOTE: the guest session is untouched on the server; the user retries
      setError(getClaimErrorMessage(caught))
    }
  }, [mergeGuestSession, rounds])

  const played = describeDemoRounds(rounds)

  return (
    <XyroDialog
      open={isOpen}
      className={styles.dialogContainer}
      isCloseButtonEnabled={false}
    >
      <div className={styles.body}>
        <UpDownNavIcon className={styles.backgroundIcon} />
        <img
          className={styles.decorations}
          src={upWinnerDecorationsPath}
          alt=''
        />

        <Flex
          className={styles.content}
          direction='column'
          align='center'
          justify='center'
          width='100%'
        >
          <div className={styles.iconWrap}>
            <CupColoredIcon
              width='11rem'
              height='11rem'
            />
          </div>

          <Text
            size='2'
            weight='bold'
            className={styles.badge}
          >
            Demo
          </Text>

          <Heading
            as='h3'
            size='7'
            align='center'
            className={styles.title}
          >
            Claim your demo results
          </Heading>

          <Text
            size='4'
            align='center'
            className={styles.description}
            data-testid={DataTestIDs.guestClaimSummary}
          >
            {played ? `${played}. ` : 'Your demo results are ready. '}
            Claim them to join the leaderboards.
          </Text>

          {error ?
            <Text
              size='2'
              align='center'
              color='red'
              data-testid={DataTestIDs.guestClaimError}
            >
              {error}
            </Text>
          : null}

          <Button
            size='4'
            color='green'
            disabled={loading}
            onClick={handleClaim}
            className={styles.claimButton}
            data-testid={DataTestIDs.buttonGuestClaim}
          >
            <XyroLoading loading={loading}>
              {error ? 'Try again' : 'Claim'}
            </XyroLoading>
          </Button>
        </Flex>
      </div>
    </XyroDialog>
  )
}
